/* eslint-disable react/no-danger */
import classNames from 'classnames';
import isEmpty from 'lodash/isEmpty';
import isNumber from 'lodash/isNumber';
import PropTypes from 'prop-types';
import React from 'react';
import Helmet from 'react-helmet';

import * as AppPropTypes from '../../lib/PropTypes';
import { addNonBreakingSpaces } from '../../lib/utils';

import Video from '../partials/Video';

import styles from '../../../styles/blocks/embed.module.scss';

const propTypes = {
    embed: AppPropTypes.embed,
    autoPlay: PropTypes.bool,
    initialMuted: PropTypes.bool,
    title: PropTypes.string,
    text: PropTypes.string,
    fullWidth: PropTypes.bool,
    className: PropTypes.string,
    isContainer: PropTypes.bool,
    containerClassName: PropTypes.string,
    videoClassName: PropTypes.string,
    onMutedChanged: PropTypes.func,
};

const defaultProps = {
    embed: null,
    autoPlay: false,
    initialMuted: false,
    title: null,
    text: null,
    fullWidth: false,
    className: null,
    isContainer: false,
    containerClassName: null,
    videoClassName: null,
    onMutedChanged: null,
};

const EmbedBlock = ({
    embed,
    autoPlay,
    initialMuted,
    title,
    text,
    fullWidth,
    className,
    isContainer,
    containerClassName,
    videoClassName,
    onMutedChanged,
}) => {
    const hasTitle = !isEmpty(title);
    const hasDescription = !isEmpty(text);
    const hasDetails = hasTitle || hasDescription;

    const { provider = null, metadata = null, iframeUrl = null } = embed || {};
    const {
        type = 'rich',
        width: embedwidth = null,
        height: embedheight = null,
        html = null,
    } = metadata || {};
    const isVideo = type === 'video';
    const isFixed = provider === '@bandcamp';

    // prettier-ignore
    const embedratio = isNumber(embedwidth) && isNumber(embedheight) && !isFixed
        ? embedwidth / embedheight
        : null;
    const hasSize =
        (embedratio !== null || (embedwidth === '100%' && isNumber(embedheight))) && !isFixed;

    const hasmaxwidth = !fullWidth && !isVideo && !isFixed && isNumber(embedwidth);

    /* prettier-disable */
    const styleBlock = {
        paddingBottom: embedratio !== null ? `${100 * embedratio}%` : null,
        maxWidth: hasmaxwidth ? embedwidth : null,
        height: embedratio === null && embedwidth === '100%' ? embedheight : null,
    };
    /* prettier-enable */

    const isProvidedVideo = provider === 'youtube' || provider === 'vimeo';
    const playerAusha = iframeUrl !== null && iframeUrl.includes('player.ausha.co');

    return !isProvidedVideo ? (
        <div
            className={classNames([
                styles.container,
                {
                    [styles.fullWidth]: fullWidth,
                    [styles.isVideo]: isVideo,
                    [styles.isFixed]: isFixed,
                    [className]: className !== null,
                },
            ])}
        >
            {provider === 'instagram' ? (
                <Helmet>
                    <script async src="//www.instagram.com/embed.js" id="instagram" />
                </Helmet>
            ) : null}

            <div
                className={classNames([
                    'blockEmbed',
                    styles.inner,
                    {
                        [styles.isContainer]: isContainer,
                        [containerClassName]: isContainer && containerClassName !== null,
                    },
                ])}
            >
                {embed !== null ? (
                    <div className={styles.embed} style={styleBlock}>
                        {iframeUrl !== null && !isFixed ? (
                            <iframe
                                src={iframeUrl}
                                className={classNames([
                                    styles.iframe,
                                    {
                                        [styles.playerAusha]: playerAusha,
                                        [styles.fullscreen]: hasSize,
                                    },
                                ])}
                                frameBorder="0"
                                allow="autoplay; encrypted-media"
                                allowFullScreen
                                title="Embed"
                                width={embedwidth}
                                height={embedheight}
                            />
                        ) : (
                            <div
                                className={classNames([
                                    styles.iframeContainer,
                                    {
                                        [styles.fullscreen]: hasSize,
                                    },
                                ])}
                                dangerouslySetInnerHTML={{ __html: html }}
                            />
                        )}
                    </div>
                ) : null}
                {hasDetails ? (
                    <div className={styles.details}>
                        {hasTitle ? (
                            <h4 className={styles.title}>{addNonBreakingSpaces(title)}</h4>
                        ) : null}
                        {hasDescription ? <p className={styles.description}>{text}</p> : null}
                    </div>
                ) : null}
            </div>
        </div>
    ) : (
        <div
            className={classNames([
                styles.inner,
                {
                    [styles.isContainer]: isContainer,
                    [containerClassName]: isContainer && containerClassName !== null,
                },
            ])}
        >
            <Video
                className={classNames([
                    styles.video,
                    {
                        [videoClassName]: videoClassName !== null,
                    },
                ])}
                iframeClassName={styles.videoIframe}
                video={embed}
                autoPlay={autoPlay}
                initialMuted={initialMuted}
                onMutedChanged={onMutedChanged}
                withEmbedControls
            />
        </div>
    );
};

EmbedBlock.propTypes = propTypes;
EmbedBlock.defaultProps = defaultProps;

export default EmbedBlock;
